import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Sparkles, Image as ImageIcon, Send, Trash2, FileText, Eye, MousePointerClick } from "lucide-react";

const SEGS = { all: "Tous les actifs", vip: "VIP", new: "Nouveaux (30j)", inactive: "Inactifs (+60j)" };

export default function ProCrmCampaigns() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ subject: "", body: "", segment: "all", image_url: "" });
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);

  const load = () => {
    api.get(`/pro/crm/campaigns`).then((r) => setItems(r.data.items || [])).catch(() => {});
    api.get(`/pro/crm/templates`).then((r) => setTemplates(r.data.items || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const applyTemplate = (t) => setForm((f) => ({ ...f, subject: t.subject, body: t.body }));

  const genText = async () => {
    if (!goal.trim()) { toast.error("Décrivez l'objectif du message à l'IA."); return; }
    setAiBusy(true);
    try { const { data } = await api.post(`/pro/crm/ai/text`, { goal, tone: "chaleureux" }); setForm((f) => ({ ...f, subject: data.subject || f.subject, body: data.body || f.body })); toast.success("Message généré ✨"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur IA"); } finally { setAiBusy(false); }
  };

  const genImage = async () => {
    const subj = goal || form.subject;
    if (!subj.trim()) { toast.error("Indiquez un objectif ou un objet pour l'image."); return; }
    setImgBusy(true);
    try { const { data } = await api.post(`/pro/crm/ai/image`, { subject: subj, style: "photo" }); setForm((f) => ({ ...f, image_url: data.url })); toast.success("Image générée 🖼️"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur image"); } finally { setImgBusy(false); }
  };

  const save = async (send) => {
    if (!form.subject.trim() || !form.body.trim()) { toast.error("Objet et message requis."); return; }
    setBusy(true);
    try {
      await api.post(`/pro/crm/campaigns`, form, { params: { send } });
      toast.success(send ? "Campagne en cours d'envoi 🚀" : "Brouillon enregistré");
      setForm({ subject: "", body: "", segment: "all", image_url: "" }); setGoal("");
      setTimeout(load, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const sendExisting = async (id) => {
    if (!window.confirm("Envoyer cette campagne maintenant ?")) return;
    try { await api.post(`/pro/crm/campaigns/${id}/send`); toast.success("Envoi lancé 🚀"); setTimeout(load, 1500); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const del = async (id) => { if (!window.confirm("Supprimer cette campagne ?")) return; try { await api.delete(`/pro/crm/campaigns/${id}`); load(); } catch { toast.error("Erreur"); } };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="crm-campaigns-page">
      <header>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900 flex items-center gap-2"><Mail size={22} className="text-[#E05638]" /> Campagnes e-mail</h1>
        <p className="text-sm text-slate-600 mt-1">Écrivez (ou laissez l'IA écrire), ajoutez une image, choisissez vos destinataires et envoyez.</p>
      </header>

      {/* Composer */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="crm-composer">
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1"><FileText size={13} /> Modèles</div>
          <div className="flex flex-wrap gap-2">
            {templates.map((t) => (
              <button key={t.key} onClick={() => applyTemplate(t)} className="px-3 py-1.5 rounded-full text-xs bg-slate-100 hover:bg-slate-200 text-slate-700" data-testid={`crm-tpl-${t.key}`}>{t.label}</button>
            ))}
          </div>
        </div>

        <div className="rounded-xl bg-orange-50/60 border border-orange-100 p-3">
          <Label className="flex items-center gap-1.5 text-[#E05638]"><Sparkles size={15} /> Assistant IA</Label>
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <Input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Ex : annoncer -20% ce week-end" data-testid="crm-ai-goal" />
            <Button type="button" variant="outline" onClick={genText} disabled={aiBusy} data-testid="crm-ai-text">{aiBusy ? "…" : "Rédiger"}</Button>
            <Button type="button" variant="outline" onClick={genImage} disabled={imgBusy} data-testid="crm-ai-image"><ImageIcon size={15} className="mr-1" />{imgBusy ? "…" : "Image"}</Button>
          </div>
        </div>

        {form.image_url && (
          <div className="relative"><img src={form.image_url} alt="" className="w-full max-h-56 object-cover rounded-xl border border-slate-200" data-testid="crm-campaign-image" />
            <button onClick={() => setForm((f) => ({ ...f, image_url: "" }))} className="absolute top-2 right-2 bg-white/90 rounded-full p-1 text-slate-600"><Trash2 size={14} /></button></div>
        )}

        <div><Label>Objet</Label><Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Objet de l'e-mail" data-testid="crm-subject" /></div>
        <div><Label>Message <span className="text-xs text-slate-400">(utilisez {"{prenom}"} et {"{pro}"})</span></Label>
          <Textarea rows={7} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} data-testid="crm-body" /></div>

        <div className="flex flex-wrap items-end gap-3 justify-between">
          <div><Label>Destinataires</Label>
            <select value={form.segment} onChange={(e) => setForm((f) => ({ ...f, segment: e.target.value }))} className="mt-1 block w-52 rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="crm-segment">
              {Object.keys(SEGS).map((s) => <option key={s} value={s}>{SEGS[s]}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => save(false)} disabled={busy} data-testid="crm-save-draft">Brouillon</Button>
            <Button onClick={() => save(true)} disabled={busy} className="btn-shine text-white" data-testid="crm-send"><Send size={15} className="mr-1" /> Envoyer</Button>
          </div>
        </div>
      </div>

      {/* Historique */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="font-heading font-extrabold text-slate-900">Mes campagnes</div>
        {items.length === 0 ? <div className="mt-2 text-sm text-slate-500">Aucune campagne pour l'instant.</div> : (
          <ul className="mt-3 divide-y divide-slate-100" data-testid="crm-campaigns-list">
            {items.map((c) => (
              <li key={c.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{c.subject}</div>
                  <div className="text-xs text-slate-500">{SEGS[c.segment] || c.segment} · {c.status === "sent" ? "envoyée" : c.status === "sending" ? "en cours d'envoi…" : "brouillon"}
                    {(c.status === "sent" || c.status === "sending") && <span className="ml-2 inline-flex items-center gap-2 flex-wrap">
                      <span>{c.stats?.sent || 0}/{c.stats?.recipients || 0} envoyé(s)</span>
                      {c.stats?.failed ? <span className="text-red-500">{c.stats.failed} échec(s)</span> : null}
                      <span className="inline-flex items-center gap-0.5"><Eye size={12} /> {c.stats?.opened || 0}</span>
                      <span className="inline-flex items-center gap-0.5"><MousePointerClick size={12} /> {c.stats?.clicked || 0}</span>
                    </span>}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {c.status !== "sent" && c.status !== "sending" && <Button size="sm" onClick={() => sendExisting(c.id)} data-testid={`crm-send-${c.id}`}><Send size={14} /></Button>}
                  <button onClick={() => del(c.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
